import useSearchSkusQuery from '@/api/queries/useSearchSkusQuery';
import Alert from '@/components/Alert';
import { SearchProps } from '@/components/search/useSearch';
import { ProductSearchFacetCode } from '@/models/Sku';
import ProductCatalogFacets from '@/modules/store/components/ProductCatalogFacets';
import ProductCatalogResults from '@/modules/store/components/ProductCatalogResults';
import { Sku, SkuAssociation } from '@/modules/store/models/Sku';

interface FacetedProductCatalogProps extends SearchProps {
  supplierIds: string[];
  productCategoryIds: string[];
  promotions: boolean;
  onToggleFacetValue: (facetCode: ProductSearchFacetCode, value: string, checked: boolean) => void;
  onClearFacets: () => void;
  onAddToCart: (sku: Sku, quantity: number) => void;
  storeId?: string | null;
}

const FacetedProductCatalog = ({
  searchQuery,
  page = 1,
  onChangePage = () => {},
  supplierIds,
  productCategoryIds,
  promotions,
  onToggleFacetValue,
  onClearFacets,
  onAddToCart,
  storeId,
}: FacetedProductCatalogProps) => {
  const { result, isLoading, isFetching, error, itemsPerPage } = useSearchSkusQuery(
    page,
    searchQuery,
    {
      supplierId: supplierIds.length > 0 ? supplierIds : undefined,
      productCategoryId: productCategoryIds.length > 0 ? productCategoryIds : undefined,
      promotions: promotions,
      status: 'ACTIVE',
    },
    [],
    [SkuAssociation.ALL],
    true,
    storeId,
    'available',
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <div className="lg:sticky lg:top-4 lg:self-start">
        <ProductCatalogFacets
          facets={result.facets}
          selection={{
            supplierIds,
            productCategoryIds,
            promotions,
          }}
          onToggleValue={onToggleFacetValue}
          onClear={onClearFacets}
        />
      </div>
      <div className="min-w-0">
        {error && (
          <div className="mb-4">
            <Alert type="error" message="Failed to load product filters. Try the classic view." />
          </div>
        )}
        <ProductCatalogResults
          skus={result}
          isLoading={isLoading}
          isFetching={isFetching}
          page={page}
          itemsPerPage={itemsPerPage}
          onChangePage={onChangePage}
          onAddToCart={onAddToCart}
        />
      </div>
    </div>
  );
};

export default FacetedProductCatalog;
